import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  CalendarCheck2,
  FileText,
  LayoutGrid,
  LogOut,
  Settings,
  Users,
} from "lucide-react";
import { useAuth } from "./providers/AuthProvider";
import { Badge, BrandLogo, Button } from "../shared-ui/primitives";
import "./AppShell.css";

/** `labelKey` resolves against the "navigation" i18n namespace at render
 * time (translation keys, not literal text) — `to` (route path) is
 * INTENTIONALLY left untranslated: URL slugs must not depend on the
 * selected UI language (plan §1.3). */
const NAV_ITEMS = [
  { to: "/pregled", labelKey: "dashboard", icon: LayoutGrid, enabled: true },
  { to: "/dnevni-unos", labelKey: "dailyEntry", icon: CalendarCheck2, enabled: true },
  { to: "/radnici", labelKey: "workers", icon: Users, enabled: true },
  { to: "/analitika", labelKey: "analytics", icon: BarChart3, enabled: true },
  { to: "/izvestaji", labelKey: "reports", icon: FileText, enabled: true },
  { to: "/sifarnici", labelKey: "catalogs", icon: BookOpen, enabled: true },
  { to: "/podesavanja", labelKey: "settings", icon: Settings, enabled: true },
] as const;

export function AppShell() {
  const { t } = useTranslation("navigation");
  const { user, company, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // A fresh `MemoryRouter`/`BrowserRouter`'s very first entry always gets the
  // literal key "default" — every later, client-side-navigated entry gets a
  // real generated key. This is the only reliable "is there anywhere to go
  // back to" signal: relying on raw browser history depth instead could
  // `navigate(-1)` the user straight out of the app (e.g. to whatever page
  // opened this PWA), since `display: "standalone"` means there is no
  // browser chrome/back button of its own to fall back on.
  const canGoBack = location.key !== "default";

  return (
    <div className="nx-shell">
      <nav className="nx-shell__nav" aria-label={t("ariaLabel")}>
        <div className="nx-shell__nav-brand">
          <BrandLogo size="sm" tone="light" />
        </div>
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.enabled ? item.to : "#"}
            className={({ isActive }) =>
              ["nx-shell__nav-item", isActive && item.enabled ? "nx-shell__nav-item--active" : "", !item.enabled ? "nx-shell__nav-item--disabled" : ""]
                .filter(Boolean)
                .join(" ")
            }
            onClick={(e) => {
              if (!item.enabled) e.preventDefault();
            }}
            aria-disabled={!item.enabled}
          >
            <item.icon size={18} aria-hidden="true" />
            <span className="nx-shell__nav-label">{t(item.labelKey)}</span>
            {!item.enabled ? <span className="nx-shell__nav-badge">{t("comingSoon")}</span> : null}
          </NavLink>
        ))}
      </nav>
      <div className="nx-shell__main">
        <header className="nx-shell__header">
          {canGoBack ? (
            <Button size="sm" variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft size={16} />
              {t("back")}
            </Button>
          ) : null}
          <div className="nx-shell__spacer" />
          {company ? <span className="nx-shell__company">{company.name}</span> : null}
          {user ? (
            <div className="nx-shell__user">
              <span>{user.email}</span>
              <Badge tone={user.role === "ADMIN" ? "accent" : "neutral"}>{user.role}</Badge>
              <Button size="sm" variant="ghost" onClick={() => logout()}>
                <LogOut size={16} />
                {t("logout")}
              </Button>
            </div>
          ) : null}
        </header>
        <main className="nx-shell__content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
